import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  LayoutDashboard,
  Key,
  FileText,
  LogOut,
  User,
  Settings,
  Activity,
  Server,
  Eye,
  Rocket,
  HardDrive,
  Monitor,
  Gauge,
  ExternalLink,
  Layers,
  Terminal,
  Globe,
  BarChart3,
  Bot
} from 'lucide-react'
import { useState, useEffect } from 'react'

interface LayoutProps {
  children: React.ReactNode
}

interface HealthStatus {
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  issueCount: number;
}

// Navigation structure with sections and their pages
const navigationSections = [
  {
    id: 'checkfirst',
    name: 'Dev',
    shortName: 'Dev',
    location: 'sidebar',
    color: {
      bg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      accent: '#10B981',
      badgeActive: 'bg-emerald-500',
      badgeInactive: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
    },
    pages: [
      { name: 'Dev Env', href: '/dev-environment', icon: Monitor },
      { name: 'Deployments', href: '/deployments', icon: Rocket },
      { name: 'AI Models', href: '/model-tiers', icon: Layers },
      { name: 'Agents', href: '/agent-runtimes', icon: Bot },
      { name: 'Scraping', href: '/scraping-providers', icon: Globe },
      { name: 'Licenses', href: '/licenses', icon: Key },
      { name: 'Plans', href: '/plans', icon: Settings },
      { name: 'Health', href: '/system-health', icon: Activity },
      { name: 'Infra Health', href: '/instance-health', icon: Server },
      { name: 'Usage', href: '/usage', icon: BarChart3 },
      { name: 'Console', href: '/console-logs', icon: Terminal },
      { name: 'Audit', href: '/audit', icon: FileText },
    ]
  },
  {
    id: 'coolify',
    name: 'Coolify',
    shortName: 'Coolify',
    location: 'sidebar',
    color: {
      bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      accent: '#0061FF',
      badgeActive: 'bg-blue-500',
      badgeInactive: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
    },
    pages: [
      { name: 'Demo Instances', href: '/demo-instances', icon: Monitor },
      { name: 'Server Monitor', href: 'http://100.65.151.125:3333', icon: Gauge, external: true },
    ]
  },
  {
    id: 'digitalocean',
    name: 'Digital\nOcean',
    shortName: 'DigitalOcean',
    location: 'sidebar',
    color: {
      bg: 'bg-gradient-to-br from-violet-500 to-purple-600',
      accent: '#7C3AED',
      badgeActive: 'bg-violet-500',
      badgeInactive: 'bg-violet-500/20 text-violet-400 hover:bg-violet-500/30'
    },
    pages: [
      { name: 'Customer Instances', href: '/customer-instances', icon: Server },
      { name: 'Backup Storage', href: '/backup-storage', icon: HardDrive },
    ]
  },
  {
    id: 'proveye',
    name: 'Prov\nEye',
    shortName: 'ProvEye',
    location: 'topbar',
    color: {
      bg: 'bg-gradient-to-br from-orange-500 to-amber-600',
      accent: '#F97316',
      badgeActive: 'bg-orange-500',
      badgeInactive: 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
    },
    pages: [
      { name: 'Intelligence', href: '/proveye', icon: Eye },
    ]
  }
]

// Split sections by location
const sidebarSections = navigationSections.filter(s => s.location === 'sidebar')
const topbarSections = navigationSections.filter(s => s.location === 'topbar')

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Find active section based on current path
  const getActiveSectionFromPath = () => {
    for (const section of navigationSections) {
      for (const page of section.pages) {
        if (page.href === location.pathname ||
            (page.href !== '/' && location.pathname.startsWith(page.href))) {
          return section.id
        }
      }
    }
    return 'coolify' // Default to first sidebar section
  }

  const [activeSection, setActiveSection] = useState(getActiveSectionFromPath())
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null)
  const [openRouterCredit, setOpenRouterCredit] = useState<string | null>(null)
  const [jevSpend, setJevSpend] = useState<string | null>(null)
  const [scrapflyCredit, setScrapflyCredit] = useState<string | null>(null)

  useEffect(() => {
    setActiveSection(getActiveSectionFromPath())
  }, [location.pathname])

  // Fetch health status for top menu badge
  useEffect(() => {
    const fetchHealthStatus = async () => {
      try {
        const token = localStorage.getItem('admin-token')
        if (!token) return
        const response = await fetch('/api/admin/health/status', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          if (data.success && data.status) {
            setHealthStatus(data.status)
          }
        }
      } catch (error) {
        console.error('Failed to fetch health status:', error)
      }
    }

    fetchHealthStatus()
    const interval = setInterval(fetchHealthStatus, 60000)
    return () => clearInterval(interval)
  }, [])

  // Fetch live credit balances for header badges
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const token = localStorage.getItem('admin-token')
        const headers: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {}
        const [orRes, sfRes, jevRes] = await Promise.all([
          fetch('/api/admin/openrouter-credits', { headers }),
          fetch('/api/admin/scrapfly-credits', { headers }),
          fetch('/api/admin/jev/spend', { headers }),
        ])
        if (jevRes.ok) {
          const data = await jevRes.json()
          if (data.success && typeof data.costCents === 'number') {
            // TypeSafe has no balance API: this is month-to-date spend computed from the instances' ledgers.
            setJevSpend(`$${(data.costCents / 100).toFixed(2)}`)
          }
        }
        if (orRes.ok) {
          const data = await orRes.json()
          if (data.success && data.credits?.remaining !== undefined) {
            setOpenRouterCredit(`$${Number(data.credits.remaining).toFixed(2)}`)
          }
        }
        if (sfRes.ok) {
          const data = await sfRes.json()
          if (data.success && data.credits?.remaining !== undefined) {
            setScrapflyCredit(`${data.credits.remaining}/${data.credits.limit}`)
          }
        }
      } catch (error) {
        console.error('Failed to fetch credits:', error)
      }
    }

    fetchCredits()
    const interval = setInterval(fetchCredits, 60000)
    return () => clearInterval(interval)
  }, [])

  const currentSection = navigationSections.find(s => s.id === activeSection)!

  const isPageActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const handleBadgeClick = (sectionId: string) => {
    setActiveSection(sectionId)
    const section = navigationSections.find(s => s.id === sectionId)
    if (section && section.pages.length > 0) {
      const firstPage = section.pages[0]
      if (!firstPage.external) {
        navigate(firstPage.href)
      }
    }
  }


  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Left Badge Navigation */}
      <div className="w-[120px] bg-slate-900 flex flex-col items-center py-4 shrink-0">
        {/* Title */}
        <div className="mb-5 text-center px-2">
          <div className="text-sm font-black text-white tracking-tight">CheckFirst</div>
          <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Admin</div>
        </div>

        {/* Section Badges - Only sidebar sections */}
        <div className="flex flex-col gap-3 px-2">
          {sidebarSections.map((section) => {
            const isActive = activeSection === section.id

            return (
              <button
                key={section.id}
                onClick={() => handleBadgeClick(section.id)}
                className={`
                  group relative w-[96px] h-[72px] rounded-xl flex items-center justify-center
                  transition-all duration-200 ease-out cursor-pointer
                  ${section.color.bg}
                  ${isActive
                    ? 'shadow-lg scale-105'
                    : 'opacity-60 hover:opacity-90 hover:scale-[1.02] shadow-md'
                  }
                `}
                style={{
                  boxShadow: isActive ? `0 0 20px ${section.color.accent}60, 0 4px 12px rgba(0,0,0,0.3)` : undefined
                }}
              >
                <span className="text-[12px] font-black text-white uppercase tracking-wide text-center leading-tight whitespace-pre-line">
                  {section.name}
                </span>

                {/* Active indicator - bright glow border */}
                {isActive && (
                  <>
                    <div
                      className="absolute inset-0 rounded-xl border-2 pointer-events-none"
                      style={{ borderColor: 'rgba(255,255,255,0.6)' }}
                    />
                    <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-10 rounded-full bg-white shadow-lg" />
                  </>
                )}
              </button>
            )
          })}
        </div>

        {/* User section at bottom */}
        <div className="mt-auto pt-4 border-t border-slate-700/50 w-full flex flex-col items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-slate-700/80 flex items-center justify-center">
            <User className="h-4 w-4 text-slate-400" />
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-red-400 transition-colors"
          >
            <LogOut className="h-3 w-3" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header - Dark theme */}
        <header className="h-14 bg-slate-900 border-b border-slate-700 shrink-0">
          <div className="h-full px-4 flex items-center justify-between">
            {/* Left: Quick Nav Badges */}
            <div className="flex items-center gap-2">
              {/* Instances Quick Access Badge - FIRST */}
              <Link
                to="/instance-settings"
                className={`
                  px-5 py-2.5 rounded-lg text-base font-extrabold transition-all flex items-center gap-2
                  ${location.pathname === '/instance-settings'
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30'
                  }
                `}
              >
                <Layers className="h-4 w-4" />
                Instances
              </Link>

              {/* Topbar sections (ProvEye) */}
              {topbarSections.map((section) => {
                const isActive = activeSection === section.id
                return (
                  <button
                    key={section.id}
                    onClick={() => handleBadgeClick(section.id)}
                    className={`
                      px-5 py-2.5 rounded-lg text-base font-extrabold transition-all
                      ${isActive
                        ? `${section.color.badgeActive} text-white shadow-md`
                        : `${section.color.badgeInactive}`
                      }
                    `}
                  >
                    {section.shortName}
                  </button>
                )
              })}

              {/* Licenses Quick Access Badge */}
              <Link
                to="/licenses"
                className={`
                  px-5 py-2.5 rounded-lg text-base font-extrabold transition-all flex items-center gap-2
                  ${location.pathname === '/licenses'
                    ? 'bg-sky-500 text-white shadow-md'
                    : 'bg-sky-500/20 text-sky-300 hover:bg-sky-500/30'
                  }
                `}
              >
                <Key className="h-4 w-4" />
                Licenses
              </Link>
            </div>

            {/* Right: Status Cards */}
            <div className="flex items-center gap-3">
              {/* OpenRouter Credit — links to analytics page */}
              <Link
                to="/openrouter-analytics"
                className="flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-all border border-slate-700"
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-emerald-400 text-sm font-bold">$</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">OpenRouter</span>
                  <span className="text-sm font-bold text-emerald-400">{openRouterCredit ?? '...'}</span>
                </div>
              </Link>

              {/* Jev (TypeSafe) month-to-date spend — links to the Jev page */}
              <Link
                to="/jev"
                className="flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-all border border-slate-700"
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <span className="text-indigo-300 text-sm font-bold">J</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Jev · month</span>
                  <span className="text-sm font-bold text-indigo-300">{jevSpend ?? '...'}</span>
                </div>
              </Link>

              {/* Scrapfly Credit */}
              <a
                href="https://scrapfly.io/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-all border border-slate-700"
              >
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <span className="text-amber-400 text-sm font-bold">⚡</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Scrapfly</span>
                  <span className="text-sm font-bold text-amber-400">{scrapflyCredit ?? '...'}</span>
                </div>
                <ExternalLink className="h-3 w-3 text-slate-500" />
              </a>

              {/* Health Status */}
              <Link
                to="/system-health"
                className="flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-all border border-slate-700"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  !healthStatus || healthStatus.status === 'HEALTHY'
                    ? 'bg-emerald-500/20'
                    : healthStatus.status === 'WARNING'
                    ? 'bg-amber-500/20'
                    : 'bg-red-500/20'
                }`}>
                  <Activity className={`h-4 w-4 ${
                    !healthStatus || healthStatus.status === 'HEALTHY'
                      ? 'text-emerald-400'
                      : healthStatus.status === 'WARNING'
                      ? 'text-amber-400'
                      : 'text-red-400'
                  }`} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Health</span>
                  <span className={`text-sm font-bold ${
                    !healthStatus || healthStatus.status === 'HEALTHY'
                      ? 'text-emerald-400'
                      : healthStatus.status === 'WARNING'
                      ? 'text-amber-400'
                      : 'text-red-400'
                  }`}>
                    {!healthStatus
                      ? 'Loading...'
                      : healthStatus.status === 'HEALTHY'
                      ? 'All Good'
                      : healthStatus.status === 'WARNING'
                      ? `${healthStatus.issueCount} Issue${healthStatus.issueCount !== 1 ? 's' : ''}`
                      : `${healthStatus.issueCount} Critical`
                    }
                  </span>
                </div>
              </Link>

              {/* Online indicator */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wide">
                  Online
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Tab Navigation for current section */}
        {currentSection.pages.length > 1 && (
          <div className="bg-white border-b-2 border-slate-200 px-3 py-2 shrink-0 overflow-x-auto">
            <div className="flex items-center gap-1.5 w-max">
              {currentSection.pages.map((page) => {
                const Icon = page.icon
                const isActive = isPageActive(page.href)

                if (page.external) {
                  return (
                    <a
                      key={page.href}
                      href={page.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all bg-slate-100 text-slate-500 hover:text-slate-700 hover:bg-slate-200 border border-slate-200"
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {page.name}
                      <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                    </a>
                  )
                }

                return (
                  <Link
                    key={page.href}
                    to={page.href}
                    className={`
                      flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border whitespace-nowrap
                      ${isActive
                        ? 'text-white shadow-md border-transparent'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                      }
                    `}
                    style={{
                      backgroundColor: isActive ? currentSection.color.accent : undefined
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {page.name}
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="p-6">
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
